import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  icons,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { ToastOptions, ToastType } from '@yada/plugin-api';
import styles from './ToastHost.module.css';

interface ParsedToast {
  id: number;
  message: string;
  title?: string;
  type: ToastType;
  iconName?: string;
  accentColor?: string;
  duration: number;
  action?: { label: string; onClick: () => void };
  isExiting?: boolean;
}

interface ToastHostProps {
  id?: string;
  bus: { on: (event: string, cb: (payload: unknown) => void) => () => void };
}

const EMOJI_FALLBACKS: Array<{ emoji: RegExp; type: ToastType; icon: string }> = [
  { emoji: /^✅\s*/u, type: 'success', icon: 'CheckCircle2' },
  { emoji: /^✨\s*/u, type: 'ai', icon: 'Sparkles' },
  { emoji: /^❌\s*/u, type: 'error', icon: 'AlertCircle' },
  { emoji: /^⚠️\s*/u, type: 'warning', icon: 'AlertTriangle' },
  { emoji: /^ℹ️?\s*/u, type: 'info', icon: 'Info' },
  { emoji: /^🤔\s*/u, type: 'ai', icon: 'Brain' },
  { emoji: /^📝\s*/u, type: 'info', icon: 'FileEdit' },
  { emoji: /^🗂️?\s*/u, type: 'info', icon: 'FolderArchive' },
  { emoji: /^🎉\s*/u, type: 'success', icon: 'PartyPopper' },
  { emoji: /^💾\s*/u, type: 'success', icon: 'Save' },
  { emoji: /^🗑️?\s*/u, type: 'warning', icon: 'Trash2' },
];

function toPascalCase(str: string): string {
  return str
    .replace(/[-_ ]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
    .replace(/^(.)/, (c) => c.toUpperCase());
}

function resolveIcon(iconName?: string, type?: ToastType): LucideIcon {
  if (iconName) {
    const pascal = toPascalCase(iconName);
    if (pascal in icons) {
      return (icons as Record<string, LucideIcon>)[pascal];
    }
  }

  switch (type) {
    case 'success':
      return CheckCircle2;
    case 'error':
      return AlertCircle;
    case 'warning':
      return AlertTriangle;
    case 'ai':
      return Sparkles;
    case 'info':
    default:
      return Info;
  }
}

function getAccentColor(type: ToastType, customColor?: string): string {
  if (customColor) return customColor;
  switch (type) {
    case 'success':
      return 'var(--success, #22c55e)';
    case 'error':
      return 'var(--error, #ef4444)';
    case 'warning':
      return 'var(--warning, #f59e0b)';
    case 'ai':
      return 'var(--accent-primary, #a855f7)';
    case 'info':
    default:
      return 'var(--accent, #10b981)';
  }
}

function parsePayload(payload: unknown, id: number): ParsedToast {
  if (typeof payload === 'object' && payload !== null && 'message' in payload) {
    const opts = payload as ToastOptions;
    return {
      id,
      message: opts.message,
      title: opts.title,
      type: opts.type ?? 'info',
      iconName: opts.icon,
      accentColor: opts.accentColor,
      duration: typeof opts.duration === 'number' ? opts.duration : 3500,
      action: opts.action,
    };
  }

  let text = String(payload ?? '');
  let detectedType: ToastType = 'info';
  let detectedIcon: string | undefined = undefined;

  for (const item of EMOJI_FALLBACKS) {
    if (item.emoji.test(text)) {
      text = text.replace(item.emoji, '');
      detectedType = item.type;
      detectedIcon = item.icon;
      break;
    }
  }

  return {
    id,
    message: text,
    type: detectedType,
    iconName: detectedIcon,
    duration: 3500,
  };
}

interface ToastItemProps {
  toast: ParsedToast;
  onDismiss: (id: number) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [, setIsHovered] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  const remainingTimeRef = useRef<number>(toast.duration);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTimer = useCallback(() => {
    if (remainingTimeRef.current <= 0) {
      onDismiss(toast.id);
      return;
    }
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismiss(toast.id);
    }, remainingTimeRef.current);
  }, [toast.id, onDismiss]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    }
  }, []);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    pauseTimer();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    startTimer();
  };

  const IconComponent = resolveIcon(toast.iconName, toast.type);
  const accent = getAccentColor(toast.type, toast.accentColor);

  return (
    <div
      id={`toast-${toast.id}`}
      className={`${styles.toast} ${toast.isExiting ? styles.exiting : ''}`}
      style={
        {
          '--toast-accent': accent,
          '--toast-duration': `${toast.duration}ms`,
        } as React.CSSProperties
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="status"
      aria-live="polite"
    >
      <div className={styles.iconWrapper}>
        <IconComponent size={16} strokeWidth={2.2} />
      </div>

      <div className={styles.content}>
        {toast.title && <div className={styles.title}>{toast.title}</div>}
        <div className={styles.message}>{toast.message}</div>

        {toast.action && (
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                toast.action?.onClick();
                onDismiss(toast.id);
              }}
            >
              {toast.action.label}
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.closeBtn}
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(toast.id);
        }}
      >
        <X size={14} strokeWidth={2} />
      </button>

      <div className={styles.progressBar} />
    </div>
  );
};

export const ToastHost: React.FC<ToastHostProps> = ({ id, bus }) => {
  const [toasts, setToasts] = useState<ParsedToast[]>([]);
  const idSeqRef = useRef(1);

  const handleDismiss = useCallback((toastId: number) => {
    setToasts((current) =>
      current.map((t) => (t.id === toastId ? { ...t, isExiting: true } : t))
    );
    setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== toastId));
    }, 200);
  }, []);

  useEffect(() => {
    const off = bus.on('toast', (payload) => {
      const toastId = idSeqRef.current++;
      const newToast = parsePayload(payload, toastId);
      setToasts((current) => [...current, newToast]);
    });
    return off;
  }, [bus]);

  return (
    <div id={id || 'toast-host'} className={styles.root}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={handleDismiss} />
      ))}
    </div>
  );
};
